import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import {
  Users, Search, TrendingUp, MousePointerClick, Shield,
  UserPlus, Lightbulb, BarChart3, ArrowRight, Mail, Settings, Sparkles,
  Activity, CheckCircle2, AlertTriangle, XCircle, RefreshCw, Globe, Zap, Lock
} from 'lucide-react';
import { format } from 'date-fns';

const adminSections = [
  { title: 'All Members', desc: 'View & manage every account', icon: Users, path: '/admin/members', color: 'text-indigo-400' },
  { title: 'New Influencer Onboarding', desc: 'Create influencer access (first-time)', icon: MousePointerClick, path: '/admin/influencer-manager', color: 'text-cyan-400' },
  { title: 'Influencer Analytics', desc: 'Click & signup tracking', icon: TrendingUp, path: '/admin/influencers', color: 'text-teal-400' },
  { title: 'Waitlist', desc: 'View signups & interest', icon: Users, path: '/admin/waitlist', color: 'text-blue-400' },
  { title: 'Founders', desc: 'Founder cohort analytics', icon: Shield, path: '/admin/founders', color: 'text-amber-400' },
  { title: 'SEO Audit', desc: 'Page-level SEO health', icon: Search, path: '/admin/seo', color: 'text-green-400' },
  { title: 'Affiliate Analytics', desc: 'Partner clicks & revenue', icon: BarChart3, path: '/admin/affiliate-analytics', color: 'text-purple-400' },
  { title: 'Referrals', desc: 'Share the Words tracking', icon: UserPlus, path: '/admin/referrals', color: 'text-pink-400' },
  { title: 'Founder Suggestions', desc: 'Review founder ideas', icon: Lightbulb, path: '/founder-suggestions', color: 'text-yellow-400' },
  { title: 'Management Tools', desc: 'Credits, affirmations & messages', icon: Settings, path: '/admin/manage', color: 'text-emerald-400' },
  { title: 'Email Automation', desc: 'Affirmations, triggers & flows', icon: Sparkles, path: '/admin/affirmations', color: 'text-orange-400' },
  { title: 'QA Dashboard', desc: 'Release gate · test status · failed checks', icon: CheckCircle2, path: '/admin/qa', color: 'text-emerald-300' },
];

interface PaidUser {
  email: string;
  full_name: string | null;
  highest_bundle: string | null;
  announcement_credits: number;
  created_at: string;
}

interface HealthReport {
  id: string;
  created_at: string;
  status: string;
  summary: {
    total_pages_checked: number;
    broken_links: number;
    healthy_links: number;
    avg_response_ms: number;
    edge_functions_checked: number;
    edge_functions_healthy: number;
    edge_functions_down: number;
    ssl_domains_checked: number;
    ssl_issues: number;
    total_issues: number;
  };
  broken_links: Array<{ url: string; status: number | null; ok: boolean; responseTimeMs: number; error?: string }>;
  edge_function_checks: Array<{ name: string; reachable: boolean; status: number | null; responseTimeMs: number; error?: string }>;
  ssl_check: { domains: Array<{ domain: string; valid: boolean; error?: string }>; all_valid: boolean };
  total_issues: number;
  duration_ms: number;
}

export default function AdminHub() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [stats, setStats] = useState({ users: 0, freeUsers: 0, paidUsers: 0, referrals: 0, influencerClicks: 0, waitlist: 0 });
  const [paidUsers, setPaidUsers] = useState<PaidUser[]>([]);
  const [healthReport, setHealthReport] = useState<HealthReport | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [runningCheck, setRunningCheck] = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase.rpc('has_role', { _user_id: user.id, _role: 'admin' }).then(({ data }) => {
      if (!data) { navigate('/dashboard'); return; }
      setIsAdmin(true);
    });
  }, [user, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    Promise.all([
      supabase.from('profiles').select('id', { count: 'exact', head: true }),
      supabase.from('referrals').select('id', { count: 'exact', head: true }),
      supabase.from('influencer_codes').select('clicks'),
      supabase.from('waitlist').select('id', { count: 'exact', head: true }),
      supabase.from('user_credits').select('has_purchased_bundle'),
      supabase.rpc('get_paid_users'),
      supabase.from('user_roles').select('user_id').eq('role', 'admin'),
    ]).then(([profiles, referrals, influencers, waitlist, credits, paid, admins]) => {
      const totalClicks = (influencers.data ?? []).reduce((s, r) => s + (r.clicks ?? 0), 0);
      const paidCount = (credits.data ?? []).filter(c => c.has_purchased_bundle).length;
      const totalUsers = profiles.count ?? 0;
      const adminEmailsCount = (admins.data ?? []).length;
      setStats({
        users: totalUsers,
        freeUsers: totalUsers - paidCount,
        paidUsers: Math.max(0, paidCount - adminEmailsCount),
        referrals: referrals.count ?? 0,
        influencerClicks: totalClicks,
        waitlist: waitlist.count ?? 0,
      });
      // Filter admins out of paid users table
      const adminIds = new Set((admins.data ?? []).map((a: { user_id: string }) => a.user_id));
      // get_paid_users returns emails only, so filter via profiles lookup
      supabase.from('profiles').select('user_id, full_name').in('user_id', Array.from(adminIds)).then(({ data: adminProfiles }) => {
        const adminNames = new Set((adminProfiles ?? []).map(p => p.full_name).filter(Boolean));
        setPaidUsers(((paid.data as PaidUser[]) ?? []).filter(pu => !adminNames.has(pu.full_name)));
      });
    });

    loadLatestReport();
  }, [isAdmin]);

  const loadLatestReport = async () => {
    setHealthLoading(true);
    const { data } = await supabase
      .from('site_health_reports')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (data) setHealthReport(data as unknown as HealthReport);
    setHealthLoading(false);
  };

  const runHealthCheck = async () => {
    setRunningCheck(true);
    try {
      const { data, error } = await supabase.functions.invoke('site-health-check');
      if (error) throw error;
      // Reload after a small delay to get the saved report
      setTimeout(() => loadLatestReport(), 1500);
    } catch (err) {
      console.error('Health check failed:', err);
    } finally {
      setRunningCheck(false);
    }
  };

  if (isAdmin === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  const summaryCards = [
    { label: 'Total Users', value: stats.users, icon: Users },
    { label: 'Free Users', value: stats.freeUsers, icon: Users },
    { label: 'Paid Users', value: stats.paidUsers, icon: Shield },
    { label: 'Referrals', value: stats.referrals, icon: UserPlus },
    { label: 'Influencer Clicks', value: stats.influencerClicks, icon: MousePointerClick },
    { label: 'Waitlist Signups', value: stats.waitlist, icon: TrendingUp },
  ];

  const bundleLabel = (b: string | null) => {
    if (!b) return 'Unknown';
    return b.charAt(0).toUpperCase() + b.slice(1);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-6xl py-8 space-y-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Admin Hub</h1>
          <p className="text-muted-foreground mt-1">Platform overview & management tools</p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {summaryCards.map((s) => (
            <Card key={s.label}>
              <CardContent className="p-4 flex items-center gap-3">
                <s.icon className="h-8 w-8 text-primary shrink-0" />
                <div>
                  <p className="text-2xl font-bold">{s.value.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Site Health Report */}
        <Card className="border-2 border-dashed border-primary/20">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Daily Site Health Check</CardTitle>
                {healthReport && (
                  <Badge variant={healthReport.total_issues === 0 ? 'default' : 'destructive'} className="ml-2">
                    {healthReport.total_issues === 0 ? '✓ Healthy' : `${healthReport.total_issues} issue${healthReport.total_issues !== 1 ? 's' : ''}`}
                  </Badge>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={runHealthCheck}
                disabled={runningCheck}
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${runningCheck ? 'animate-spin' : ''}`} />
                {runningCheck ? 'Running…' : 'Run Now'}
              </Button>
            </div>
            <CardDescription>
              Auto-runs daily at 1:00 AM Eastern
              {healthReport && ` · Last check: ${format(new Date(healthReport.created_at), 'MMM d, yyyy h:mm a')}`}
              {healthReport && ` · Took ${(healthReport.duration_ms / 1000).toFixed(1)}s`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {healthLoading ? (
              <div className="flex justify-center py-8">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : healthReport ? (
              <div className="space-y-6">
                {/* Health Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="rounded-lg border p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Globe className="h-4 w-4 text-blue-500" />
                      <span className="text-sm font-medium">Pages</span>
                    </div>
                    <p className="text-xl font-bold text-foreground">
                      {healthReport.summary.healthy_links}/{healthReport.summary.total_pages_checked}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {healthReport.summary.avg_response_ms}ms avg
                    </p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Zap className="h-4 w-4 text-amber-500" />
                      <span className="text-sm font-medium">Functions</span>
                    </div>
                    <p className="text-xl font-bold text-foreground">
                      {healthReport.summary.edge_functions_healthy}/{healthReport.summary.edge_functions_checked}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {healthReport.summary.edge_functions_down > 0
                        ? `${healthReport.summary.edge_functions_down} down`
                        : 'All reachable'}
                    </p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Lock className="h-4 w-4 text-green-500" />
                      <span className="text-sm font-medium">SSL</span>
                    </div>
                    <p className="text-xl font-bold text-foreground">
                      {healthReport.ssl_check.all_valid ? '✓ Valid' : '✗ Issues'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {healthReport.summary.ssl_domains_checked} domain{healthReport.summary.ssl_domains_checked !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                      <span className="text-sm font-medium">Total Issues</span>
                    </div>
                    <p className={`text-xl font-bold ${healthReport.total_issues === 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {healthReport.total_issues}
                    </p>
                    <p className="text-xs text-muted-foreground">across all checks</p>
                  </div>
                </div>

                {/* Broken Links Detail */}
                {healthReport.summary.broken_links > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-red-600 mb-2 flex items-center gap-1">
                      <XCircle className="h-4 w-4" /> Broken Links
                    </h4>
                    <div className="space-y-1">
                      {healthReport.broken_links.filter(l => !l.ok).map((link, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm rounded bg-red-50 dark:bg-red-950/20 px-3 py-1.5">
                          <Badge variant="destructive" className="text-xs">{link.status ?? 'ERR'}</Badge>
                          <span className="text-muted-foreground truncate">{link.url}</span>
                          {link.error && <span className="text-xs text-red-500">{link.error}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Edge Functions Detail */}
                {healthReport.summary.edge_functions_down > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-amber-600 mb-2 flex items-center gap-1">
                      <AlertTriangle className="h-4 w-4" /> Unreachable Functions
                    </h4>
                    <div className="space-y-1">
                      {healthReport.edge_function_checks.filter(f => !f.reachable).map((fn, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm rounded bg-amber-50 dark:bg-amber-950/20 px-3 py-1.5">
                          <Badge variant="outline" className="text-xs border-amber-500 text-amber-700">DOWN</Badge>
                          <span className="font-mono text-muted-foreground">{fn.name}</span>
                          {fn.error && <span className="text-xs text-amber-600">{fn.error}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* SSL Issues */}
                {!healthReport.ssl_check.all_valid && (
                  <div>
                    <h4 className="text-sm font-semibold text-red-600 mb-2 flex items-center gap-1">
                      <Lock className="h-4 w-4" /> SSL Issues
                    </h4>
                    <div className="space-y-1">
                      {healthReport.ssl_check.domains.filter(d => !d.valid).map((d, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm rounded bg-red-50 dark:bg-red-950/20 px-3 py-1.5">
                          <Badge variant="destructive" className="text-xs">INVALID</Badge>
                          <span className="text-muted-foreground">{d.domain}</span>
                          {d.error && <span className="text-xs text-red-500">{d.error}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* All Good Message */}
                {healthReport.total_issues === 0 && (
                  <div className="flex items-center gap-3 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 px-4 py-3">
                    <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-green-800 dark:text-green-300">All systems operational</p>
                      <p className="text-xs text-green-600 dark:text-green-400">
                        {healthReport.summary.total_pages_checked} pages, {healthReport.summary.edge_functions_checked} functions, and SSL all healthy.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Activity className="h-8 w-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No health check reports yet. Click "Run Now" to generate the first one.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Paid Users Table */}
        {paidUsers.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-primary" />
                    <CardTitle className="text-lg">Paid Users</CardTitle>
                  </div>
                  <CardDescription>{paidUsers.length} user{paidUsers.length !== 1 ? 's' : ''} with purchased bundles (admins excluded)</CardDescription>
                </div>
                <Button asChild variant="outline" size="sm">
                  <Link to="/admin/members">View All Members <ArrowRight className="h-3 w-3 ml-1" /></Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Bundle</TableHead>
                    <TableHead className="text-right">Credits</TableHead>
                    <TableHead>Joined</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paidUsers.map((pu) => (
                    <TableRow key={pu.email}>
                      <TableCell className="font-medium">{pu.email}</TableCell>
                      <TableCell>{pu.full_name || '—'}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{bundleLabel(pu.highest_bundle)}</Badge>
                      </TableCell>
                      <TableCell className="text-right">{pu.announcement_credits}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {pu.created_at ? format(new Date(pu.created_at), 'MMM d, yyyy') : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Section Navigation */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {adminSections.map((section) => (
            <Link key={section.path} to={section.path}>
              <Card className="h-full transition-all hover:shadow-elevation-2 hover:border-primary/30 group cursor-pointer">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <section.icon className={`h-6 w-6 ${section.color}`} />
                    <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <CardTitle className="text-base">{section.title}</CardTitle>
                  <CardDescription>{section.desc}</CardDescription>
                </CardHeader>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
